import { CrateDrop, CrateKind, LevelData, MAX_COLS, MAX_MONKS, MAX_ROWS, T, cellIndex } from './types'

const TILE_CHARS: Record<string, T> = {
  '.': T.Empty, '#': T.Brick, '=': T.Concrete, 'H': T.Ladder, '-': T.Rope,
  'x': T.FalseBrick, 'I': T.Ice, 's': T.Sand, 'c': T.Crumble, 'L': T.Lava,
  '<': T.ConveyorL, '>': T.ConveyorR, '^': T.Trampoline, 'D': T.DoorClosed,
  'n': T.ConveyorUp, 'w': T.ConveyorDown, 'R': T.BombRefill, '(': T.TrampolineL, ')': T.TrampolineR, // Lot C
  'Z': T.DeathZone, // #1 : champ mortel traversable (non-solide, tue au contact partout)
  'A': T.TrampolineStrong, // trampoline PUISSANT (reste de la demande #7 : amplitude paramétrable ; rebond 5 cases vs 3)
  '~': T.Water, // EAU (16/07) : non-solide, nage 4 directions + souffle (spec 2026-07-16-eau-design)
  '+': T.Fire,   // FEU (16/07) : non-solide, mortel au contact (coureur + moine), version VISIBLE de la zone mortelle
  '"': T.Spikes, // PICS (16/07) : non-solides, mortels UNIQUEMENT en TOMBANT dessus (marche latérale = sans danger)
  '/': T.RampUpRight, // RAMPE (16/07) : solide, monte vers la DROITE en diagonale (hop 8 ticks)
  '\\': T.RampUpLeft, // RAMPE (16/07) : solide, monte vers la GAUCHE en diagonale
  ':': T.Bubbles, // SOURCE DE BULLES (16/07 soir) : à poser au FOND de l'eau ; SEULE cette case recharge le souffle (les bulles qui montent sur ~3 cases sont un visuel)
  '_': T.Checkpoint, // CHECKPOINT ver de terre (16/07 soir) : ANCRE = centre du ver de 5 cases, non solide ; le toucher = point de réanimation (spec 2026-07-16-checkpoint-ver-design)
  '{': T.TrampolineStrongL, // trampoline directionnel PUISSANT haut-GAUCHE (16/07 soir, C1) : hauteur de 'A' + dérive de '('
  '}': T.TrampolineStrongR, // trampoline directionnel PUISSANT haut-DROITE : complète la matrice force x direction
  // ---- SECOND ALPHABET (17/07) : l'ASCII visible est épuisé (il restait ` et ') — on passe aux
  // symboles Latin-1 SÛRS : 1 unité UTF-16 (line[c] et length marchent tels quels), AUCUNE forme
  // décomposée NFD (jamais de « é » : 1 ou 2 unités selon la normalisation !). Zéro changement
  // d'infra : UTF-8 de bout en bout (JSON, SQLite TEXT, fichiers). Premiers occupants :
  '°': T.Button, // INTERRUPTEUR : non solide ; occupé (coureur/moine/caisse) = circuit PRESSÉ
  '¦': T.Barrier, // BARRIÈRE : SOLIDE fermée ; le circuit pressé la mute en BarrierOpen (traversable)
  '»': T.OneWayR, // SENS UNIQUE gauche->droite : non solide ; l'entrée par la DROITE est un mur (horizontal seulement)
  '«': T.OneWayL, // SENS UNIQUE droite->gauche : miroir
  '¤': T.DashCrystal, // CRISTAL DE DASH : non solide, non consommé ; en cooldown, le traverser remet le dash à zéro
}
// Caisses poussables (Phase 4) : char ASCII -> (kind, contenu). Parsées en ENTITÉS (la case reste
// Empty, comme un spawn). Chars renommés le 16/07 : les initiaux (C W B Y K P) recouvraient des
// chars de la couche DÉCO (cristal, toile, bannière, valve, crâne, panneau) et l'éditeur route le
// pinceau par char -> poser une caisse posait une déco. Alphabet désormais DISJOINT de DECOS
// (garanti par tests/chars-registry.test.ts). Mnémonique : X métal (incassable), F bois (Fragile),
// O Or, N Nitro (bombe), J marteau (Jack), y tazer (Y inversé), Q clé (anneau+tige), d corde.
const CRATE_CHARS: Record<string, { kind: CrateKind; drop: CrateDrop }> = {
  'X': { kind: 'metal', drop: null },
  'F': { kind: 'wood', drop: null },
  'O': { kind: 'wood', drop: 'gold' },
  'N': { kind: 'wood', drop: 'bomb' },
  'J': { kind: 'wood', drop: 'jackhammer' },
  'y': { kind: 'wood', drop: 'tazer' },
  'Q': { kind: 'wood', drop: 'key' },
  'd': { kind: 'wood', drop: 'rope' },
  ']': { kind: 'boulder', drop: null }, // ROCHER (17/07) : rond, roule quand on le pousse, écrase tout
}
const TP_PAIRS: [string, string][] = [['T', 't'], ['U', 'u'], ['V', 'v']]

// Registre COMPLET des caractères réservés de la GRILLE PRINCIPALE (tuiles + caisses + objets,
// spawns, moines, téléporteurs du switch de levelFromAscii). Tout NOUVEAU caractère — quelle que
// soit la couche, déco comprise : l'éditeur route le pinceau PAR CHAR — doit être absent d'ici
// ET de DECOS (tests/chars-registry.test.ts fait respecter la règle depuis la collision du 16/07).
export const LEVEL_CHARS: string[] = [
  ...Object.keys(TILE_CHARS), ...Object.keys(CRATE_CHARS),
  '$', 'E', 'b', 'j', 'z', 'k', 'r', 'æ', '1', '2', '3', 'm', 'S', 'G', 'M', ',', ';', 'µ', '`', 'ð', 'þ', 'ø', 'œ', 'Ŧ', 'Ł', 'Ŋ', 'ĸ', 'Ħ',
  ...TP_PAIRS.flat(),
]

export function levelFromAscii(name: string, lines: string[]): LevelData {
  // Lot E : dimensions DÉDUITES de l'ASCII (au lieu d'assérer 16x28). Grille rectangulaire
  // (toutes les lignes de même longueur), non vide, dans les bornes MAX_COLS x MAX_ROWS.
  const rows = lines.length
  if (rows === 0) throw new Error(`niveau "${name}" : grille vide (aucune ligne)`)
  const cols = lines[0]!.length
  if (cols === 0) throw new Error(`niveau "${name}" : grille vide (ligne 1 sans caractère)`)
  if (cols > MAX_COLS) throw new Error(`niveau "${name}" : ${cols} colonnes, trop large (max ${MAX_COLS})`)
  if (rows > MAX_ROWS) throw new Error(`niveau "${name}" : ${rows} lignes, trop haute (max ${MAX_ROWS})`)
  const terrain = new Uint8Array(rows * cols)
  const golds: number[] = [], exitCells: number[] = []
  const monkSpawns: LevelData['monkSpawns'] = []
  const fishSpawns: LevelData['fishSpawns'] = []
  const drapeauSpawns: number[] = [] // DRAPEAU-CRAPAUD (05/08) : cases du char 'ð'
  const mailletSpawns: number[] = [] // MAILLET À RESSORT (05/08) : cases du char 'œ'
  const patateSpawns: number[] = []  // PATATE CHAUDE (05/08) : cases du char 'Ŧ'
  const couronneSpawns: number[] = [] // COURONNE MAUDITE (05/08) : cases du char 'Ħ'
  const items: LevelData['items'] = []
  const crates: LevelData['crates'] = []
  let spawnP1 = -1, spawnP2 = -1, spawnP3 = -1
  const tpSeen = new Map<string, number[]>()
  // Or/objets posés DANS l'eau (16/07 nuit, demande Claude-Alain) : une entité n'est pas une
  // tuile — si la case AU-DESSUS est de l'eau (ou une source de bulles), l'eau « coule » sur la
  // case de l'entité. Sinon, poser une pièce dans un bassin y creusait une BULLE D'AIR (recharge
  // de souffle gratuite + rupture de nage). Fiable car le parse va de HAUT en BAS : la rangée
  // au-dessus (eau des chars '~'/':' comme celle posée par les poissons ','/';') est déjà écrite.
  const dunk = (cell: number): void => {
    const above = cell >= cols ? terrain[cell - cols] : T.Empty
    if (above === T.Water || above === T.Bubbles) terrain[cell] = T.Water
  }
  lines.forEach((line, r) => {
    if (line.length !== cols) throw new Error(`niveau "${name}" ligne ${r + 1} : ${line.length} caractères au lieu de ${cols}`)
    for (let c = 0; c < cols; c++) {
      const ch = line[c]!, cell = cellIndex(c, r, cols)
      if (ch in TILE_CHARS) { terrain[cell] = TILE_CHARS[ch]!; continue }
      if (ch in CRATE_CHARS) { const cs = CRATE_CHARS[ch]!; crates.push({ cell, kind: cs.kind, drop: cs.drop }); continue } // caisse -> entité (case Empty)
      switch (ch) {
        case '$': golds.push(cell); dunk(cell); break
        case 'E': exitCells.push(cell); break
        case 'b': items.push({ cell, kind: 'bomb' }); dunk(cell); break
        case 'j': items.push({ cell, kind: 'jackhammer' }); dunk(cell); break
        case 'z': items.push({ cell, kind: 'tazer' }); dunk(cell); break
        case 'k': items.push({ cell, kind: 'key' }); dunk(cell); break
        case 'r': items.push({ cell, kind: 'rope' }); dunk(cell); break // objet corde (#4)
        // APPÂT (31/07) : 'a' et '&' sont pris par la couche déco. L'ASCII lisible est épuisé
        // depuis longtemps ; on prend une lettre Latin-1 SÛRE (1 unité UTF-16, aucune forme NFD
        // décomposable), comme 'µ' l'a été pour le bâtisseur. Vérifié libre par chars-registry.
        case 'æ': items.push({ cell, kind: 'appat' }); dunk(cell); break
        // POTION DE GÉANT (05/08, objet versus n°2). 'þ' : même famille Latin-1 sûre que 'µ'/'æ'/'ð'.
        case 'þ': items.push({ cell, kind: 'potion' }); dunk(cell); break
        // DÉGUISEMENT DE MOINE (05/08, objet versus n°3). 'ø' : même famille.
        case 'ø': items.push({ cell, kind: 'deguisement' }); dunk(cell); break
        case '1': spawnP1 = cell; break
        case '2': spawnP2 = cell; break
        case '3': spawnP3 = cell; break
        case 'm': monkSpawns.push({ cell, kind: 'normal' }); break
        case 'S': monkSpawns.push({ cell, kind: 'sauteur' }); break
        case 'G': monkSpawns.push({ cell, kind: 'voleur' }); break
        case 'M': monkSpawns.push({ cell, kind: 'boss' }); break
        case 'µ': monkSpawns.push({ cell, kind: 'batisseur' }); break // BÂTISSEUR (17/07, 2e alphabet : ']' est au rocher) : il rebouche les trous
        case '`': monkSpawns.push({ cell, kind: 'normal', dormant: true }); break // STATUE au gong d'or (17/07)
        // DRAPEAU-CRAPAUD (05/08, objet versus n°1). 'ð' : lettre Latin-1 SÛRE (1 unité UTF-16,
        // aucune forme NFD décomposable), comme 'µ' et 'æ' avant elle — vérifié par chars-registry.
        case 'ð': drapeauSpawns.push(cell); break
        // MAILLET À RESSORT (05/08, objet versus n°4). 'œ' : même famille Latin-1 sûre.
        case 'œ': mailletSpawns.push(cell); break
        // PATATE CHAUDE (05/08, objet versus n°6) : posée ÉTEINTE, elle attend son premier imprudent.
        case 'Ŧ': patateSpawns.push(cell); break
        // BANDELETTE (05/08, objet versus n°5) : l'objet qui se ramasse ; le PIÈGE se pose à l'usage.
        case 'Ł': items.push({ cell, kind: 'bandelette' }); dunk(cell); break
        // MEUTE PRIVÉE (05/08, objet versus n°7) et FANTÔME (n°9) : objets de slot.
        case 'Ŋ': items.push({ cell, kind: 'meute' }); dunk(cell); break
        case 'ĸ': items.push({ cell, kind: 'fantome' }); dunk(cell); break
        // COURONNE MAUDITE (05/08, objet versus n°8) : entité au sol, elle se coiffe au contact.
        case 'Ħ': couronneSpawns.push(cell); break
        // Ennemis AQUATIQUES (16/07 soir) : le char pose l'entité ET SON EAU (un seul geste
        // dans l'éditeur — un poisson sans eau n'existe pas).
        case ',': fishSpawns.push({ cell, kind: 'piranha' }); terrain[cell] = T.Water; break
        case ';': fishSpawns.push({ cell, kind: 'meduse' }); terrain[cell] = T.Water; break
        default: {
          const pair = TP_PAIRS.find(([a, b]) => ch === a || ch === b)
          if (!pair) throw new Error(`niveau "${name}" : caractère inconnu "${ch}" ligne ${r + 1}`)
          terrain[cell] = T.Teleporter
          const key = pair[0]
          tpSeen.set(key, [...(tpSeen.get(key) ?? []), cell])
        }
      }
    }
  })
  const teleporters: [number, number][] = []
  for (const cells of tpSeen.values()) if (cells.length === 2) teleporters.push([cells[0]!, cells[1]!])
  for (const cells of tpSeen.values()) if (cells.length !== 2) teleporters.push([cells[0]!, -1]) // signalé par validateLevel
  return { name, cols, rows, terrain, golds: golds.sort((a, b) => a - b), items, crates, monkSpawns, fishSpawns, drapeauSpawns, mailletSpawns, patateSpawns, couronneSpawns, spawnP1, spawnP2, spawnP3, teleporters, exitCells }
}

/** Vrai si la grille contient un départ Joueur 2 (« 2 ») : condition d'éligibilité versus/coop. */
export function hasSpawnP2(rows: string[]): boolean {
  return rows.some((line) => line.includes('2'))
}

/** Vrai si la grille a un départ Joueur 3 (« 3 ») : éligibilité d'un salon à 3 joueurs. */
export function hasSpawnP3(rows: string[]): boolean {
  return rows.some((line) => line.includes('3'))
}

/** Nombre de départs joueurs présents (1, 2 ou 3) : borne le nombre de joueurs d'un salon. */
export function spawnCount(rows: string[]): number {
  return ['1', '2', '3'].filter((c) => rows.some((line) => line.includes(c))).length
}

export function validateLevel(l: LevelData): string[] {
  const pb: string[] = []
  if (l.spawnP1 < 0) pb.push('spawn joueur 1 manquant')
  // Or au sol OU or caché dans une caisse bois (Phase 4) : un niveau qui n'a que de l'or en caisse reste jouable.
  if (l.golds.length === 0 && !l.crates.some((c) => c.drop === 'gold')) pb.push('aucune pièce d\'or')
  if (l.monkSpawns.length > MAX_MONKS) pb.push(`trop de moines (${l.monkSpawns.length} > ${MAX_MONKS})`)
  // Ennemis aquatiques (16/07 soir) : un poisson HORS de l'eau ne bougera jamais (il ne vit que
  // dans Water/Bubbles) — possible seulement sur un LevelData construit à la main (le char pose
  // son eau), mais on le signale. Et on borne la meute comme les moines.
  for (const f of l.fishSpawns) {
    const t = l.terrain[f.cell]
    if (t !== T.Water && t !== T.Bubbles) pb.push('poisson hors de l\'eau (il ne bougera pas)')
  }
  if (l.fishSpawns.length > 8) pb.push(`trop de poissons (${l.fishSpawns.length} > 8)`)
  if (l.teleporters.some(([, b]) => b < 0)) pb.push('téléporteur non apparié')
  if (l.exitCells.length === 0) pb.push('échelle de sortie manquante')
  // Porte fermée sans AUCUNE clé nulle part (au sol ou en caisse) : elle ne s'ouvrira jamais. Si elle
  // barre le seul chemin vers l'or, le niveau est infinissable — et rien ne le disait (audit 16/07).
  // On ne signale que le zéro absolu : moins de clés que de portes peut être un choix (« un seul
  // passage sur trois »).
  const doors = l.terrain.some((t) => t === T.DoorClosed)
  const keys = l.items.some((i) => i.kind === 'key') || l.crates.some((c) => c.drop === 'key')
  if (doors && !keys) pb.push('porte fermée sans aucune clé')
  return pb
}
